/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useCallback, useMemo, useState } from "react";
import dayjs from "dayjs";
import { IUser } from "../../../../store/api/admin/types";
import { useStoreDispatch } from "../../../../store/hooks";
import { useSelector } from "react-redux";
import { confirmDialogSelector } from "../../../../store/helpers/helperSelectors";
import { setConfirmDialog } from "../../../../store/helpers/helperReducers";
import { setIsCreateUser } from "../../../../store/api/admin/reducers";
import { DATEFORMATS, DIALOGOPENFOR } from "../../../../globals/globals";
import CustomDataGrid from "../../../Shared/Table/CustomDataGrid";
import SearchBox from "../../../Shared/SearchBox/SearchBox";
import { RadixSelectControl } from "../../../ui/radix-select";
import { Button } from "../../../ui/button";
import { FilterX, MoreVertical } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "../../../ui/tooltip";
import ActiveFilterChips from "../../../Shared/Filter/ActiveFilterChips";
import { resetKeyword, resetKeywordForScope } from "../../../../store/search/reducer";
import { searchKeywordSelector, searchKeywordByScopeSelector } from "../../../../store/search/selector";
import { Icon } from "../../../Shared/Icon";
import { useCreateUserForm } from "./hooks";
import ConfirmDialog from "../../../Shared/Dialogs/ConfirmDialog";
import AddUpdateUserInfo from "./AddUpdateUserInfo";
import { resetUser } from "../../../helpers";
import { getFutureDate } from "../../../../utils/helpers";
import { getUsersColumns } from "../../../Shared/Table/Columns";


const USERS_SEARCH_SCOPE = "users_grid_search";

const Users: React.FC = () => {
  const dispatch = useStoreDispatch();
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const searchKeyword = useSelector(searchKeywordByScopeSelector(USERS_SEARCH_SCOPE));

  const [profileFilter, setProfileFilter] = useState<string>("All");
  const [userToDelete, setUserToDelete] = useState<IUser | null>(null);
  const [showUserForm, setShowUserForm] = useState(false);

  // Get form data and handlers
  const {
    data: users = [],
    profiles,
    deleteUserById,
    errMsg,
    setErrMsg,
    userFormData,
    setUserFormData,
    passExpireDate,
    setPassExpireDate,
    passNeverExpire,
    setPassNeverExpire,
    isChangePass,
    setIsChangePass,
    submitHandler,
    isLoadingStatus,
    handleClickAction,
    isCreateUser,
    onBlurHandler,
    cancelHandler,
    username,
    password,
    confirmPassword,
    passwordExpiryDate,
    firstName,
    lastName,
    department,
    location,
    phone,
    email,
  } = useCreateUserForm();

  // Filter users based on search and profile filter
  const filteredUsers = useMemo(() => {
    let filtered = users;

    // Filter by search keyword
    if (searchKeyword && searchKeyword.trim()) {
      const keyword = searchKeyword.toLowerCase();
      filtered = filtered.filter((user: IUser) =>
        user.username?.toLowerCase().includes(keyword) ||
        user.email?.toLowerCase().includes(keyword) ||
        user.firstName?.toLowerCase().includes(keyword) ||
        user.lastName?.toLowerCase().includes(keyword) ||
        user.department?.toLowerCase().includes(keyword) ||
        user.location?.toLowerCase().includes(keyword) ||
        user.phone?.toLowerCase().includes(keyword)
      );
    }

    // Filter by profile
    if (profileFilter !== "All") {
      filtered = filtered.filter((user: IUser) => user.profile?.id?.toString() === profileFilter);
    }

    return filtered;
  }, [users, searchKeyword, profileFilter]);

  const showReset = profileFilter !== "All" || (!!searchKeyword && searchKeyword.trim().length > 0);

  const handleClearFilters = useCallback(() => {
    setProfileFilter("All");
    dispatch(resetKeywordForScope({ scope: USERS_SEARCH_SCOPE }));
  }, [dispatch]);

  const handleClearSearch = useCallback(() => {
    dispatch(resetKeywordForScope({ scope: USERS_SEARCH_SCOPE }));
  }, [dispatch]);

  const userFilters = useMemo(
    () => [
      {
        id: "profile",
        label: "Profile",
        value: profileFilter !== "All" ? profileFilter : "",
        setValue: (() => { setProfileFilter("All"); return { type: "NOOP" }; }) as any,
        values: (profiles?.data || []).map((profile: any) => ({
          value: profile.id?.toString(),
          text: profile.name,
        })),
      },
    ],
    [profileFilter, profiles?.data]
  );

  const handleDeleteUser = useCallback((user: IUser) => {
    // Store user to delete and show confirmation dialog
    setUserToDelete(user);
    dispatch(setConfirmDialog(DIALOGOPENFOR.DELETEUSER));
  }, [dispatch]);

  const handleRemoveAction = async (actionName: string) => {
    if (!userToDelete || !actionName) return;

    try {
      // Call deleteUserById with the user data
      await deleteUserById({ value: userToDelete.id, text: userToDelete.username });
      setUserToDelete(null);
      // Close the sheet after successful deletion
      setTimeout(() => {
        setShowUserForm(false);
      }, 500);
    } catch (error) {
      console.error("Error deleting user:", error);
      setUserToDelete(null);
    }
  };

  const handleEditUser = useCallback((user: IUser, isChangePass: boolean = false) => {
    // Set the user data and open form in edit mode
    dispatch(setIsCreateUser(false));
    setUserFormData({ ...user, profileId: user?.profile?.id || 0 });
    setErrMsg({});

    // Initialize password-related fields from user data
    if (user?.passExpireDate) {
      setPassExpireDate(dayjs(user.passExpireDate));
    }

    // Set passNeverExpire: if passExpireDate is 0 then checked, else unchecked
    setPassNeverExpire(user?.passExpireDate === 0);

    // Delay sheet opening to allow dropdown to close first
    setTimeout(() => {
      setShowUserForm(true);
    }, 300);

    if (isChangePass) {
      setIsChangePass(true);
    } else {
      setIsChangePass(false);
    }
  }, [dispatch]);

  const handleAddUser = () => {
    // Reset form data for new entry
    dispatch(setIsCreateUser(true));
    setUserFormData(resetUser as IUser);
    setErrMsg({});
    setIsChangePass(false);
    setPassExpireDate(dayjs(getFutureDate(30)));
    setPassNeverExpire(false);
    // Delay sheet opening to ensure clean state initialization
    setTimeout(() => {
      setShowUserForm(true);
    }, 300);
  };

  const handleCloseUserForm = () => {
    setShowUserForm(false);
    cancelHandler();
  };

  // Wrapper around handleClickAction to close sheet after delete
  const handleClickActionWithClose = async (actionName: string) => {
    await handleClickAction(actionName);
    // Close sheet after successful delete (with delay to allow animations)
    setTimeout(() => {
      setShowUserForm(false);
    }, 500);
  };

  const columns = useMemo(
    () => getUsersColumns(handleEditUser, handleDeleteUser),
    [handleEditUser, handleDeleteUser]
  );

  return (
    <div className="overflow-x-hidden m-3 mt-4 mr-3">
      <div>
        <div className="flex items-center justify-between flex-wrap gap-x-4 gap-y-2 w-full max-w-full overflow-x-hidden mt-2">
          <div className="flex items-center flex-wrap gap-2 min-w-0 max-w-full flex-1">
            <div style={{ marginRight: 1 }} className="shrink-0">
              <SearchBox tooltip="Search users..." placeholder="Search users..." scope={USERS_SEARCH_SCOPE} width={270} />
            </div>
            <div style={{ width: "140px" }}>
              <RadixSelectControl
                value={profileFilter === "All" ? "__placeholder__" : profileFilter}
                onValueChange={(val) => setProfileFilter(val === "__placeholder__" ? "All" : val)}
                options={[
                  { value: "__placeholder__", label: "Profiles" },
                  ...(profiles?.data || []).map((profile: any) => ({
                    value: profile.id?.toString(),
                    label: profile.name,
                  })),
                ]}
                placeholder="Profiles"
              />
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="inline-flex items-center justify-center rounded-md text-sm h-8 w-8 bg-muted transition-colors duration-150 hover:bg-muted"
                  onClick={handleClearFilters}
                  aria-label="Reset All"
                  style={{ visibility: showReset ? "visible" : "hidden", marginLeft: "7px" }}
                >
                  <FilterX className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Reset All</TooltipContent>
            </Tooltip>
          </div>
          <div className="flex items-center shrink-0 ml-auto">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90" variant="outline" onClick={() => handleAddUser()}>
              <Icon name="add" size={18} />
              Add User
            </Button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2 shrink-0 mt-6">
        <div className="flex flex-wrap items-center gap-0">
          <span className="text-sm text-muted-foreground mb-1">{` ${filteredUsers?.length || 0} results`}</span>
          <ActiveFilterChips
            filters={userFilters}
            searchKeyword={searchKeyword}
            onClearSearch={handleClearSearch}
            onClearAll={handleClearFilters}
            dispatch={dispatch}
          />
        </div>
      </div>


      <div className="w-full">
        <CustomDataGrid
          tableId="usersGrid"
          sx={{
            "& .MuiDataGrid-row.Mui-selected": {
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "transparent",
              },
            },
          }}
          rows={filteredUsers}
          columns={columns}
          rowCount={filteredUsers.length}
          loading={false}
          paginationMode="client"
          sortingMode="client"
          disableRowSelectionOnClick
          disableColumnMenu
          fillContainer
        />
      </div>


      {/* Delete Confirmation Dialog */}
      {openConfirmDialog === DIALOGOPENFOR.DELETEUSER && userToDelete && (
        <ConfirmDialog actionName="Yes, Delete" handleClickAction={handleRemoveAction}>
          Are you sure you want to delete user <strong>{userToDelete.username}</strong>?.
        </ConfirmDialog>
      )}

      {/* Add/Edit User Form Sheet */}
      <AddUpdateUserInfo
        open={showUserForm}
        onOpenChange={setShowUserForm}
        profileData={profiles?.data || []}
        errMsg={errMsg}
        setErrMsg={setErrMsg}
        userFormData={userFormData}
        setUserFormData={setUserFormData}
        passExpireDate={passExpireDate}
        setPassExpireDate={setPassExpireDate}
        isChangePass={isChangePass}
        setIsChangePass={setIsChangePass}
        submitHandler={submitHandler}
        deleteHandler={() => {
          dispatch(setConfirmDialog(DIALOGOPENFOR.USER));
        }}
        handleClickAction={handleClickActionWithClose}
        isCreateUser={isCreateUser}
        onBlurHandler={onBlurHandler}
        passNeverExpire={passNeverExpire}
        setPassNeverExpire={setPassNeverExpire}
        isLoadingStatus={isLoadingStatus}
        cancelHandler={handleCloseUserForm}
        username={username}
        password={password}
        confirmPassword={confirmPassword}
        passwordExpiryDate={passwordExpiryDate}
        firstName={firstName}
        lastName={lastName}
        department={department}
        location={location}
        phone={phone}
        email={email}
      />
    </div>
  );
};

export default Users;
